import jsPDF from "jspdf";
import {
    brand,
    drawFrame,
    drawLogoPreserved,
    caseTransform,
    priceLabel,
} from "./pdfBrandHelpers";

const CATEGORY_ORDER = [
    "Antipasti",
    "Primi",
    "Secondi",
    "Pizze",
    "Contorni",
    "Dolci",
    "Bevande",
];

/**
 * PDF "Menù Ristorante" — A4, brand-aware, multi-pagina, 1 o 2 colonne.
 * - Niente banda colorata, cornice in stile, logo proporzionato.
 * - Sezioni per categoria, niente righe di divisione tra i piatti.
 * - Eventuale "Costo coperto" mostrato in fondo solo se > 0.
 *
 * @param {object} menu
 * @param {string} menu.title
 * @param {string} menu.restaurant_name
 * @param {number} [menu.coperto] - 0 = non mostrato
 * @param {1|2} [menu.columns=2]
 * @param {string} [menu.message]
 * @param {Array<{category:string, dishes: Array<{name,description,allergens,price_eur}>}>} menu.sections
 * @param {object} brandOpts
 */
export function downloadRestaurantMenuPdf(menu, brandOpts = {}) {
    const B = brand(brandOpts);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    const H = 297;
    const margin = 22;
    const contentW = W - margin * 2;
    const { primary, accent, ink, muted, paper } = B.rgb;
    const STYLE = B.style;
    const F = B.fonts;
    const cols = menu.columns === 1 ? 1 : 2;
    const gutter = 10;
    const colW = (contentW - gutter * (cols - 1)) / cols;

    // Ordina sezioni secondo CATEGORY_ORDER, mantieni eventuali altre alla fine
    const sections = sortSections(menu.sections || []);

    const startY = margin;
    const bottomY = H - 20; // riservato a coperto + footer
    let firstPage = true;

    const paintBg = () => {
        doc.setFillColor(...paper);
        doc.rect(0, 0, W, H, "F");
        drawFrame(doc, W, H, margin, STYLE, B.rgb);
    };

    paintBg();

    // ===== Header pagina 1 (logo o nome + titolo + messaggio) =====
    let headerY = startY;

    if (B.useLogo && B.logoDataUrl) {
        const used = drawLogoPreserved(doc, B.logoDataUrl, W / 2, headerY, 60, 28);
        headerY += (used || 0) + 6;
    } else if (menu.restaurant_name) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setFontSize(14);
        doc.setTextColor(...primary);
        doc.text(
            caseTransform(menu.restaurant_name, STYLE.nameCase),
            W / 2,
            headerY + 6,
            { align: "center" },
        );
        headerY += 12;
    }

    // Titolo principale
    doc.setFont(F.title.family, F.title.weight);
    doc.setFontSize(STYLE.titleSize);
    doc.setTextColor(...primary);
    // Sposta headerY alla baseline del titolo per evitare overlap con logo/nome
    headerY += STYLE.titleSize * 0.42;
    const titleText = caseTransform(menu.title || "Menù", STYLE.nameCase === "upper" ? "upper" : "normal");
    const titleLines = doc.splitTextToSize(titleText, contentW);
    titleLines.forEach((line, i) => {
        doc.text(line, W / 2, headerY + i * (STYLE.titleSize * 0.42), { align: "center" });
    });
    headerY += titleLines.length * (STYLE.titleSize * 0.42);

    // Decorazione sotto titolo
    if (STYLE.id === "classic" || STYLE.id === "vintage") {
        doc.setDrawColor(...accent);
        doc.setLineWidth(STYLE.id === "vintage" ? 0.8 : 0.4);
        doc.line(W / 2 - 14, headerY + 2, W / 2 + 14, headerY + 2);
        headerY += 6;
    } else {
        headerY += 4;
    }

    if (menu.message) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setFontSize(10.5);
        doc.setTextColor(...muted);
        const msgLines = doc.splitTextToSize(menu.message, contentW - 20);
        msgLines.forEach((line, i) => {
            doc.text(line, W / 2, headerY + i * 5, { align: "center" });
        });
        headerY += msgLines.length * 5 + 4;
    }

    // ===== Layout colonne =====
    // y di partenza dopo l'header (solo prima pagina)
    let colIdx = 0;
    let y = headerY + 4;
    const colTopOther = startY + 4; // y in cima alle altre pagine
    const colX = (c) => margin + c * (colW + gutter);

    const newColumn = () => {
        colIdx += 1;
        if (colIdx >= cols) {
            // nuova pagina
            doc.addPage();
            paintBg();
            colIdx = 0;
            y = colTopOther;
            firstPage = false;
        } else {
            y = firstPage ? headerY + 4 : colTopOther;
        }
    };

    const measureWrappedHeight = (text, font, weight, size, width) => {
        doc.setFont(font, weight);
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(text || "", width);
        return { lines, h: lines.length * (size * 0.42) };
    };

    const writeText = (text, x, font, weight, size, color, width, lineHeightFactor = 0.42) => {
        doc.setFont(font, weight);
        doc.setFontSize(size);
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(text, width);
        lines.forEach((line, i) => {
            doc.text(line, x, y + i * (size * lineHeightFactor));
        });
        y += lines.length * (size * lineHeightFactor);
    };

    sections.forEach((sec) => {
        if (!sec.dishes || sec.dishes.length === 0) return;

        // header categoria
        const x = colX(colIdx);

        // Spazio minimo per header + 1 piatto
        const minBlock = 28;
        if (y + minBlock > bottomY) newColumn();

        // Eyebrow categoria
        doc.setFont(F.title.family, F.title.weight);
        doc.setFontSize(10);
        doc.setTextColor(...accent);
        doc.text(caseTransform(sec.category, "upper"), x, y);
        y += 2;
        doc.setDrawColor(...accent);
        doc.setLineWidth(STYLE.id === "vintage" ? 0.7 : 0.3);
        doc.line(x, y + 1.5, x + Math.min(colW, 38), y + 1.5);
        y += 7;

        sec.dishes.forEach((d) => {
            const priceText = priceLabel(d.price_eur);

            // calcola altezza necessaria (name + desc + allergens)
            doc.setFont(F.price.family, F.price.weight);
            doc.setFontSize(STYLE.nameSize - 2);
            const priceW = priceText ? doc.getTextWidth(priceText) + 4 : 0;

            const nameMeta = measureWrappedHeight(
                caseTransform(d.name || "—", STYLE.nameCase),
                F.dish.family,
                F.dish.weight,
                STYLE.nameSize - 2,
                colW - priceW,
            );

            let totalNeeded = nameMeta.h + 1;
            let descLines = [];
            if (d.description) {
                const descMeta = measureWrappedHeight(
                    d.description,
                    F.subtitle.family,
                    F.subtitle.weight,
                    9.2,
                    colW,
                );
                descLines = descMeta.lines;
                totalNeeded += descMeta.h + 0.5;
            }
            let allergText = "";
            let allergLines = [];
            if (d.allergens?.length) {
                allergText = `allergeni: ${d.allergens.join(" · ")}`;
                const allergMeta = measureWrappedHeight(
                    allergText,
                    F.allergens.family,
                    F.allergens.weight,
                    7.5,
                    colW,
                );
                allergLines = allergMeta.lines;
                totalNeeded += allergMeta.h + 0.5;
            }

            // page break check
            if (y + totalNeeded + 6 > bottomY) {
                newColumn();
            }

            const curX = colX(colIdx);

            // nome
            doc.setFont(F.dish.family, F.dish.weight);
            doc.setFontSize(STYLE.nameSize - 2);
            doc.setTextColor(...primary);
            const nameText = caseTransform(d.name || "—", STYLE.nameCase);
            const nameLines = doc.splitTextToSize(nameText, colW - priceW);
            doc.text(nameLines, curX, y);

            // prezzo allineato a destra della colonna
            if (priceText) {
                doc.setFont(F.price.family, F.price.weight);
                doc.setFontSize(STYLE.nameSize - 2);
                doc.setTextColor(...accent);
                doc.text(priceText, curX + colW, y, { align: "right" });
            }
            y += nameLines.length * ((STYLE.nameSize - 2) * 0.42) + 0.5;

            if (descLines.length) {
                doc.setFont(F.subtitle.family, F.subtitle.weight);
                doc.setFontSize(9.2);
                doc.setTextColor(...ink);
                descLines.forEach((line, i) => {
                    doc.text(line, curX, y + i * 4);
                });
                y += descLines.length * 4;
            }

            if (allergLines.length) {
                doc.setFont(F.allergens.family, F.allergens.weight);
                doc.setFontSize(7.5);
                doc.setTextColor(...muted);
                allergLines.forEach((line, i) => {
                    doc.text(line, curX, y + i * 3.4);
                });
                y += allergLines.length * 3.4;
            }

            // Whitespace tra piatti (NO linea)
            y += STYLE.spacing - 4;
        });

        // spazio extra dopo la categoria
        y += 3;
    });

    // ===== Coperto + Footer su ogni pagina =====
    const pages = doc.internal.getNumberOfPages();
    const coperto = Number(menu.coperto) || 0;
    for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        // Coperto solo sull'ultima pagina, se > 0
        if (p === pages && coperto > 0) {
            doc.setFont(F.price.family, F.price.weight);
            doc.setFontSize(10);
            doc.setTextColor(...primary);
            doc.text(
                `Coperto e servizio: ${priceLabel(coperto)} a persona`,
                W / 2,
                H - 17,
                { align: "center" },
            );
        }
        doc.setFont(F.allergens.family, F.allergens.weight);
        doc.setFontSize(7);
        doc.setTextColor(...muted);
        doc.text(
            "Allergeni: legge UE 1169/2011 — informazioni dettagliate disponibili presso il personale",
            W / 2,
            H - 11,
            { align: "center" },
        );
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...accent);
        doc.text(`${p}/${pages}`, W - margin, H - 11, { align: "right" });
    }

    const safe = (menu.title || "Menu Ristorante")
        .replace(/[^a-zA-Z0-9 _-]/g, "")
        .trim()
        .replace(/\s+/g, "_");
    doc.save(`${safe || "Menu_Ristorante"}.pdf`);
}

function sortSections(sections) {
    const known = [];
    const unknown = [];
    sections.forEach((s) => {
        if (!s || !s.category) return;
        if (CATEGORY_ORDER.includes(s.category)) known.push(s);
        else unknown.push(s);
    });
    known.sort(
        (a, b) =>
            CATEGORY_ORDER.indexOf(a.category) -
            CATEGORY_ORDER.indexOf(b.category),
    );
    return [...known, ...unknown];
}
